import { z } from "zod/v4";
import { createEndpointBase } from "./core";
import {
  ExecutionJobLogSchema,
  ExecutionLogOverviewSchema,
  ExecutionLogSchemas,
  ExecutionLogsStatsSchema,
  ExecutionStepLogSchemas,
} from "../ea.schema";
import { CountResponseSchema } from "../ea.schema.endpoints";
import {
  ExecutionJobLogsFilterSchema,
  ExecutionLogsFilterSchema,
  ExecutionLogsStatsFilterSchema,
  GetRequestBaseResponseSchema,
  StepLogsFilterSchema,
} from "../ea.schema.query";
import type { Statistic } from "../ea.types";

const base = "logs";
const logs = createEndpointBase(base);
const logsExecution = createEndpointBase(base, "execution");
const logsStep = createEndpointBase(base, "step");
const logsJobAggregated = createEndpointBase(base, "job", "aggregated");

export const logsEndpoints = {
  logsExecutionGet: logsExecution({
    method: "GET",
    path: "",
    request: z.object({ filter: ExecutionLogsFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(ExecutionLogSchemas["general"]),
    description: "Retrieves a list of execution logs with filtering support.",
  }),
  logsExecutionCount: logsExecution({
    method: "GET",
    path: "count",
    request: z.any(),
    response: CountResponseSchema,
    description: "Returns the total number of execution logs matching the filters.",
  }),
  logsExecutionOverview: logsExecution({
    method: "GET",
    path: "overview",
    request: z.object({ ids: z.array(z.coerce.number()) }),
    response: ExecutionLogOverviewSchema.array(),
    description: "Retrieves an overview of execution logs for specified IDs.",
  }),
  logsExecutionProject: logsExecution({
    method: "POST",
    path: "project",
    request: z.object({ projectId: z.coerce.number() }),
    response: z.any(),
    responseType: {} as Statistic[],
    description: "Retrieves execution statistics for a specific project.",
  }),
  logsExecutionStats: logsExecution({
    method: "GET",
    path: "stats",
    request: z.object({ filter: ExecutionLogsStatsFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(ExecutionLogsStatsSchema),
    responseType: {} as Statistic[],
    description: "Retrieves execution log statistics grouped by status.",
  }),
  logsExecutionCapturedFiles: logsExecution({
    method: "GET",
    path: ":id/capturedFiles",
    request: z.any(),
    response: z.any(),
    description: "Retrieves a list of files captured during execution.",
  }),
  logsExecutionDownloadCapturedFile: logsExecution({
    method: "POST",
    path: "download/:sessionId/capturedFiles/:filename",
    request: z.any(),
    response: z.any(),
    description: "Downloads a specific captured file.",
  }),
  logsExecutionUploadCapturedFile: logsExecution({
    method: "POST",
    path: "upload/:sessionId/capturedFiles",
    request: z.any(),
    response: z.any(),
    requestType: {} as {
      file?: any;
    },
    description: "Uploads a captured file for a session.",
  }),
  logsStepScreenshot: logsStep({
    method: "GET",
    path: "screenshot/:sessionId/:fileName",
    request: z.any(),
    response: z.any(),
    description: "Retrieves a screenshot for a specific session and file name.",
  }),
  logsStepGet: logsStep({
    method: "GET",
    path: "",
    request: z.object({ filter: StepLogsFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(ExecutionStepLogSchemas["general"]),
    description: "Retrieves a list of step execution logs with filtering support.",
  }),
  logsStepCount: logsStep({
    method: "GET",
    path: "count",
    request: z.any(),
    response: CountResponseSchema,
    description: "Returns the total number of step execution logs matching the filters.",
  }),
  logsStepExecutionScreenshots: logsStep({
    method: "GET",
    path: "executionScreenshots",
    request: z.object({ id: z.coerce.number() }),
    response: z.any(),
    description: "Retrieves screenshots for a specific step execution.",
  }),
  logsStepLinkedMap: logsStep({
    method: "GET",
    path: "linkedMap",
    request: z.object({ executionId: z.coerce.number() }),
    response: z.any(),
    description: "Retrieves a linked map of step execution logs.",
  }),

  // Aggregated Job Logs
  logsAggregatedJobGet: logsJobAggregated({
    method: "GET",
    path: "",
    request: z.object({ filter: ExecutionJobLogsFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(ExecutionJobLogSchema),
    description: "Retrieves a list of aggregated job logs with filtering support.",
  }),
  logsAggregatedJobCount: logsJobAggregated({
    method: "GET",
    path: "count",
    request: z.any(),
    response: CountResponseSchema,
    description: "Returns the total number of aggregated job logs matching the filters.",
  }),
  logsAggregatedJobExclusion: logsJobAggregated({
    method: "POST",
    path: "exclusion",
    request: z.object({ jobExecutionIds: z.array(z.string()) }),
    response: z.any(),
    description: "Manages exclusions for aggregated job logs.",
  }),
} as const;

// SmartAny Examples:
//
// Normal requestType (enforced):
// path = ":sessionId/:fileName" + requestType = { sessionId: string, fileName: string, data: any }
// Result: { sessionId: string, fileName: string, data: any } (strict - all required)
//
// requestType = any (smart hints):
// path = ":platformId/:userId" + requestType = any
// Result: { platformId?: string | number, userId?: string | number } & Record<string, any>
// ↑ IntelliSense shows path params as optional hints, but accepts any object without errors
